"use client";
import React, { useEffect, useState, useRef } from "react";
import { Chat } from "stream-chat-react";
import { StreamChat } from "stream-chat";
import { useAppDispatch, useAppSelector } from "@/lib/store/hooks";
import { useSession } from "next-auth/react";
import { getUserChatId } from "@/lib/store/features/profile/profileSlice";
// import { toast } from "sonner"; // Removed unused import

export const GetStreamProvider = ({ children }: { children: React.ReactNode }) => {
  const { userChatId, userChatToken } = useAppSelector((state) => state.profile);
  const session = useSession();

  const [client, setClient] = useState<StreamChat | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const connectionAttempts = useRef(0);
  const maxConnectionAttempts = 3;
  const [subscribedChannels, setSubscribedChannels] = useState<Set<string>>(new Set()); // Track subscribed channels
  const lastRateLimitTime = useRef(0); // Track when we last hit rate limit
  const rateLimitBackoffMs = useRef(1000); // Exponential backoff for rate limits
  const isConnecting = useRef(false); // Prevent multiple simultaneous connection attempts
  const hasInitialized = useRef(false); // Track if client has been initialized

  // Audio file path for message notifications
  const MESSAGE_ALERT_PATH = "/next/next_assets/audio/happy-message-ping-351298.mp3";

  // Rate limiting for audio playback to avoid 429 errors
  const [lastAudioPlayTime, setLastAudioPlayTime] = useState(0);
  const AUDIO_COOLDOWN_MS = 1000; // Minimum 1 second between audio plays

  // Optimized function to create notification for new message
  const createNotificationForMessage = async (
    message: any,
    senderName: string,
    channelId: string
  ) => {
    try {
      // Optimization: Check if we already created notification for this message
      const notificationKey = `notification_${message.id}_${userChatId}`;
      if (sessionStorage.getItem(notificationKey)) {
        return;
      }

      const response = await fetch("/next/api/stream-chat/test-message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          senderId: message.user?.id,
          senderName: senderName,
          recipientId: userChatId,
          messageText: message.text || "New message",
          channelId: channelId,
        }),
      });

      if (response.ok) {
        // Optimization: Mark as created to prevent duplicates
        sessionStorage.setItem(notificationKey, Date.now().toString());
      }
    } catch (error) {
      // Error creating notification for message
    }
  };

  // Configuration to avoid 429 errors - set to true to use only Web Audio API
  const USE_WEB_AUDIO_ONLY = true; // Set to true to avoid file requests entirely

  // Function to create a channel directly in Stream Chat (bypassing broken API)
  const createChannelDirectly = async (recipientId: string, recipientName: string) => {
    return await createCleanChannel(recipientId, recipientName);
  };

  // Function to ensure user is subscribed to a channel
  const ensureChannelSubscription = async (channelId: string) => {
    if (!client || !channelId) return;

    try {
      // Clean the channel ID - remove the channel type prefix if present
      let cleanChannelId = channelId;
      if (channelId.includes(":")) {
        cleanChannelId = channelId.split(":")[1];
      }

      // Check if already subscribed
      if (subscribedChannels.has(cleanChannelId)) {
        return;
      }

      // Get the channel (don't create, just get existing)
      const channel = client.channel("messaging", cleanChannelId);

      // Watch the channel to receive messages
      await channel.watch();

      // Add to subscribed channels
      setSubscribedChannels((prev) => new Set(prev).add(cleanChannelId));

      // Store channel info in sessionStorage
      const channelInfo = {
        channelId: cleanChannelId,
        recipientId: cleanChannelId
          .replace(`messaging_${userChatId}_`, "")
          .replace(`messaging_${userChatId}`, ""),
        recipientName: "User",
        createdAt: new Date().toISOString(),
        lastAccessed: new Date().toISOString(),
        isStreamChatChannel: true, // Mark as Stream Chat created channel
      };

      const existingChannels = JSON.parse(sessionStorage.getItem("userChannels") || "[]");
      const updatedChannels = [
        ...existingChannels.filter((c: any) => c.channelId !== cleanChannelId),
        channelInfo,
      ];
      sessionStorage.setItem("userChannels", JSON.stringify(updatedChannels));
    } catch (error: any) {
      // Error subscribing to channel
    }
  };

  // Function to restore channels from sessionStorage
  const restoreChannelsFromStorage = async () => {
    if (!client || !userChatId) return;

    try {
      const storedChannels = JSON.parse(sessionStorage.getItem("userChannels") || "[]");

      for (const channelInfo of storedChannels) {
        const { channelId, isStreamChatChannel } = channelInfo;

        try {
          if (isStreamChatChannel) {
            // For Stream Chat created channels, just subscribe to them
            await ensureChannelSubscription(channelId);
          } else {
            // For manually created channels, use the old method
            const { recipientId, recipientName } = channelInfo;
            const channel = await createCleanChannel(recipientId, recipientName);
          }
        } catch (error: any) {
          // Error restoring channel from storage
        }
      }
    } catch (error: any) {
      // Error restoring channels from sessionStorage
    }
  };

  // Function to restore channel subscriptions after page refresh
  const restoreChannelSubscriptions = async () => {
    if (!client || !userChatId) return;

    try {
      // Step 1: Load channels from database (primary source)
      const dbResult = await loadUserChannelsFromDatabase();

      // Step 2: Check if we have channels after database load
      const currentSubscribedCount = subscribedChannels.size;

      if (currentSubscribedCount === 0) {
        // Try to find channels via Stream Chat queries
        try {
          const filter = { members: { $in: [userChatId] } };
          const sort = [{ last_message_at: -1 }];
          const channels = await client.queryChannels(filter, sort, { limit: 20 });

          // Subscribe to each channel found via Stream Chat
          for (const channel of channels) {
            const channelId = channel.id;
            if (!channelId) continue;

            try {
              // Watch the channel
              await channel.watch();

              // Add to subscribed channels
              setSubscribedChannels((prev) => new Set(prev).add(channelId));

              // Get other members (excluding current user)
              const members = Object.keys(channel.state.members || {});
              const otherMembers = members.filter((memberId) => memberId !== userChatId);
              const recipientId = otherMembers[0] || "unknown";

              // Store channel info in sessionStorage
              const channelInfo = {
                channelId,
                recipientId,
                recipientName: `User ${recipientId}`,
                createdAt: new Date().toISOString(),
                lastAccessed: new Date().toISOString(),
                isStreamChatChannel: true,
              };

              const existingChannels = JSON.parse(sessionStorage.getItem("userChannels") || "[]");
              const updatedChannels = [
                ...existingChannels.filter((c: any) => c.channelId !== channelId),
                channelInfo,
              ];
              sessionStorage.setItem("userChannels", JSON.stringify(updatedChannels));
            } catch (error: any) {
              // Error subscribing to chat channel
            }
          }
        } catch (error: any) {
          // Error querying chat channels
        }
      }

      // Step 3: Final fallback to sessionStorage if still no channels
      const finalSubscribedCount = subscribedChannels.size;
      if (finalSubscribedCount === 0) {
        await restoreChannelsFromStorage();
      }
    } catch (error: any) {
      // Error restoring channel subscriptions
    }
  };

  // Create a simple, short notification sound using Web Audio API
  const createNotificationSound = () => {
    if (typeof window !== "undefined" && window.AudioContext) {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800Hz tone
        oscillator.type = "sine";

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime); // Lower volume
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1); // Fade out

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1); // Very short duration (100ms)

        return true;
      } catch (error: any) {
        // Web Audio API failed
        return false;
      }
    }
    return false;
  };

  // Enhanced audio playback with rate limiting and fallback
  const playNotificationSoundSafely = () => {
    const now = Date.now();

    // Rate limiting: don't play audio too frequently
    if (now - lastAudioPlayTime < AUDIO_COOLDOWN_MS) {
      return;
    }

    // Update last play time
    setLastAudioPlayTime(now);

    // Try Web Audio API first (most reliable, no file requests)
    if (createNotificationSound()) {
      return;
    }

    // Only try audio file if Web Audio API fails AND we're not in Web Audio only mode
    if (!USE_WEB_AUDIO_ONLY && audio) {
      try {
        audio.currentTime = 0;
        audio
          .play()
          .then(() => {
            // Audio file notification played successfully
          })
          .catch((error: any) => {
            // Audio file play failed

            // Handle specific error types
            if (error.name === "NotAllowedError") {
              setUserHasInteracted(true);
            } else if (error.name === "AbortError" || error.message?.includes("429")) {
              // Reset the last play time to allow retry after cooldown
              setLastAudioPlayTime(now - AUDIO_COOLDOWN_MS + 2000); // Allow retry in 2 seconds
            }
          });
      } catch (error: any) {
        // Audio file error
      }
    } else if (USE_WEB_AUDIO_ONLY) {
      // Web Audio only mode - no file fallback available
    } else {
      // No audio object available for notification
    }
  };
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

  // Create audio object and set up user interaction listeners when component mounts
  useEffect(() => {
    if (typeof window !== "undefined" && !USE_WEB_AUDIO_ONLY) {
      const audioObj = new Audio(MESSAGE_ALERT_PATH);

      // Add event listeners to debug audio loading
      audioObj.addEventListener("loadstart", () => {});
      audioObj.addEventListener("canplay", () => {});
      audioObj.addEventListener("error", (e) => {
        // Check if it's a 429 error or network issue
        const target = e.target as HTMLAudioElement;
        if (target.error?.code === 4 || target.networkState === 3) {
          // Don't try fallback file if we're getting network errors
          return;
        }

        // Try fallback to old audio file only for non-network errors
        audioObj.src = "/next/next_assets/audio/message_alert.mp3";
      });

      // Add additional error handling for network issues
      audioObj.addEventListener("abort", () => {});

      audioObj.addEventListener("stalled", () => {});

      setAudio(audioObj);
    }

    // Set up user interaction listeners immediately (needed for Web Audio API too)
    const handleUserInteraction = () => {
      setUserHasInteracted(true);

      // Remove listeners after first interaction
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };

    // Listen for user interactions to enable audio
    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);

    // Cleanup function
    return () => {
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };
  }, []);
  const [soundEnabled, setSoundEnabled] = useState(true); // User preference for sound
  const [userHasInteracted, setUserHasInteracted] = useState(false); // Track user interaction

  // Test function to manually trigger audio (for debugging)
  const testAudio = () => {
    if (soundEnabled) {
      playNotificationSoundSafely();
    }
  };

  // Test function to check channel subscriptions
  const testChannelSubscriptions = () => {
    // Test function for channel subscriptions
  };

  // Test function to manually restore channel subscriptions
  const testRestoreSubscriptions = () => {
    restoreChannelSubscriptions().catch((error) => {
      // Manual restoration failed
    });
  };

  // Quick test to check if user has any channels
  const quickChannelTest = async () => {
    if (!client || !userChatId) {
      return;
    }

    try {
      // Try a simple query with shorter timeout
      const filter = { members: { $in: [userChatId] } };

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Quick test timeout")), 5000); // 5 second timeout
      });

      const queryPromise = client.queryChannels(filter, [], { limit: 5 });
      const channels = (await Promise.race([queryPromise, timeoutPromise])) as any;

      return channels.length;
    } catch (error: any) {
      return 0;
    }
  };

  // Test function to send a message and create a channel
  const testSendMessage = async (recipientId: string, messageText: string = "Test message") => {
    if (!client || !userChatId) {
      return;
    }

    try {
      // Create channel first with proper permissions
      const channel = await createOrGetChannel(recipientId, "Test User");
      if (!channel) {
        return false;
      }

      // Check if user is a member of the channel
      const members = Object.keys(channel.state.members || {});

      // Ensure user is a member
      if (!members.includes(userChatId)) {
        try {
          await channel.addMembers([userChatId], {
            user_id: userChatId,
          });
        } catch (memberError: any) {
          return false;
        }
      }

      // Send message
      const messageResponse = await channel.sendMessage({
        text: messageText,
      });

      return true;
    } catch (error: any) {
      return false;
    }
  };

  // Expose test function globally for debugging
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).testAudio = testAudio;
      (window as any).enableAudio = () => {
        setUserHasInteracted(true);
      };
      (window as any).getAudioState = () => {
        return { soundEnabled, hasAudio: !!audio, userHasInteracted, audioSrc: audio?.src };
      };
      (window as any).testChannelSubscriptions = testChannelSubscriptions;
      (window as any).testRestoreSubscriptions = testRestoreSubscriptions;
      (window as any).quickChannelTest = quickChannelTest; // Expose the new test function
      (window as any).createChannelDirectly = createChannelDirectly; // Expose channel creation function
      (window as any).createOrGetChannel = createOrGetChannel; // Expose comprehensive channel function
      (window as any).createCleanChannel = createCleanChannel; // Expose clean channel creation
      (window as any).deleteAllChannelsFromStream = deleteAllChannelsFromStream; // Expose channel deletion
      (window as any).clearAllChannels = clearAllChannels; // Expose channel clearing function
      (window as any).forceSubscribeToChannel = forceSubscribeToChannel; // Expose force subscription function
      (window as any).testSendMessage = testSendMessage; // Expose test message function
      (window as any).testChannelForBothUsers = testChannelForBothUsers; // Expose both users test
      (window as any).testStreamChatChannelSystem = testStreamChatChannelSystem; // Expose Stream Chat system test
      (window as any).discoverAndSubscribeToUserChannels = discoverAndSubscribeToUserChannels; // Expose channel discovery
      (window as any).createChannelViaAPI = createChannelViaAPI; // Expose server-side channel creation
      (window as any).loadUserChannelsFromDatabase = loadUserChannelsFromDatabase; // Expose database channel loading
      (window as any).subscribeToChannel = subscribeToChannel; // Expose channel subscription
      (window as any).updateLastMessageTimestamp = updateLastMessageTimestamp; // Expose timestamp update
      // Expose Stream Chat client with better error handling
      (window as any).getStreamProviderClient = () => {
        if (!client) {
          return null;
        }
        return client;
      };
      (window as any).restoreChannelsFromStorage = restoreChannelsFromStorage; // Expose sessionStorage restoration
      (window as any).debugSessionStorage = debugSessionStorage; // Expose sessionStorage debugging
      (window as any).forceRestoreChannels = forceRestoreChannels; // Expose force restoration
      (window as any).getStreamProviderState = () => ({
        hasClient: !!client,
        isConnected,
        userChatId,
        subscribedChannelsCount: subscribedChannels.size,
        subscribedChannelIds: Array.from(subscribedChannels),
        isReady: !!client && isConnected && !!userChatId,
        initializationStatus: client ? "ready" : "initializing",
      });

      // Expose a ready check function
      (window as any).isStreamProviderReady = () => {
        return !!client && isConnected && !!userChatId;
      };

      // Expose a wait for ready function
      (window as any).waitForStreamProviderReady = (timeoutMs = 10000) => {
        return new Promise((resolve, reject) => {
          const startTime = Date.now();

          const checkReady = () => {
            if (!!client && isConnected && !!userChatId) {
              resolve(true);
              return;
            }

            if (Date.now() - startTime > timeoutMs) {
              reject(new Error("Stream Provider not ready within timeout"));
              return;
            }

            setTimeout(checkReady, 100);
          };

          checkReady();
        });
      };
      (window as any).testChannelRestoration = async () => {
        await restoreChannelSubscriptions();

        return {
          before: subscribedChannels.size,
          after: subscribedChannels.size,
          success: subscribedChannels.size > 0,
        };
      };
      (window as any).forceAddUserToChannels = forceAddUserToChannels; // Expose force add user to channels
      (window as any).checkDatabaseChannels = async () => {
        try {
          const response = await fetch("/next/api/stream-chat/conversations");
          const data = await response.json();

          return {
            success: data.success,
            conversationsCount: data.conversations?.length || 0,
            conversations: data.conversations || [],
            error: data.error,
          };
        } catch (error: any) {
          return {
            success: false,
            error: error.message,
          };
        }
      };
      (window as any).testChannelCreation = async (
        recipientId: string,
        recipientName: string = "Test User"
      ) => {
        try {
          // Test creating a channel
          const channelId = await createCleanChannel(recipientId, recipientName);

          if (channelId) {
            // Check current state
            const state = (window as any).getStreamProviderState();

            return {
              success: true,
              channelId,
              subscribedChannelsCount: state.subscribedChannelsCount,
              subscribedChannelIds: state.subscribedChannelIds,
            };
          } else {
            return { success: false, error: "Channel creation returned null" };
          }
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      };
      (window as any).testPageRefreshScenario = async () => {
        try {
          // Simulate the scenario: user has channels in DB but subscription fails
          const currentState = (window as any).getStreamProviderState();

          // Check database
          const dbResult = await (window as any).checkDatabaseChannels();

          // Try to restore channels
          const restorationResult = await (window as any).testChannelRestoration();

          // Check final state
          const finalState = (window as any).getStreamProviderState();

          return {
            success: finalState.subscribedChannelsCount > 0,
            initialChannels: currentState.subscribedChannelsCount,
            finalChannels: finalState.subscribedChannelsCount,
            dbConversations: dbResult.conversationsCount,
            restorationSuccess: restorationResult.success,
            error:
              finalState.subscribedChannelsCount === 0
                ? "No channels subscribed after restoration"
                : null,
          };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      };
    }
  }, [audio, soundEnabled, userHasInteracted, client, isConnected, userChatId, subscribedChannels]);
  const dispatch = useAppDispatch();

  const apiKey = process.env.NEXT_PUBLIC_STREAM_CLIENT_KEY as string; // Should match STREAM_CLIENT_KEY

  // Handle page visibility changes to maintain connection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && client && !isConnected) {
        connectionAttempts.current = 0; // Reset connection attempts
      }
    };

    // Also handle page load/focus events for better reconnection
    const handlePageFocus = () => {
      if (client && !isConnected) {
        connectionAttempts.current = 0; // Reset connection attempts
      }
    };

    // Handle chat open events to ensure channel subscription
    const handleChatOpen = (event: CustomEvent) => {
      const { channelId } = event.detail;
      if (channelId && client) {
        ensureChannelSubscription(channelId);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handlePageFocus);
    window.addEventListener("open-chat-popup", handleChatOpen as EventListener);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handlePageFocus);
      window.removeEventListener("open-chat-popup", handleChatOpen as EventListener);
    };
  }, [client, isConnected]);

  useEffect(() => {
    if (!userChatId || !userChatToken || !apiKey) {
      return;
    }

    // If we've already initialized, don't do it again
    if (hasInitialized.current) {
      return;
    }

    // If we already have a connected client, don't reinitialize
    if (client && isConnected) {
      return;
    }

    // If we've already initialized, don't do it again
    if (hasInitialized.current) {
      return;
    }

    // Prevent multiple simultaneous connection attempts
    if (isConnecting.current) {
      return;
    }

    // Check if we've exceeded max connection attempts
    if (connectionAttempts.current >= maxConnectionAttempts) {
      connectionAttempts.current = 0; // Reset for page refresh
    }

    // Check if we're currently in a rate limit backoff period
    const now = Date.now();
    const timeSinceLastRateLimit = now - lastRateLimitTime.current;
    if (timeSinceLastRateLimit < rateLimitBackoffMs.current) {
      const remainingBackoff = rateLimitBackoffMs.current - timeSinceLastRateLimit;

      setTimeout(() => {
        initClient();
      }, remainingBackoff);
      return;
    }

    const initClient = async () => {
      isConnecting.current = true;
      connectionAttempts.current++;

      // console.log("Initializing Stream Chat client with API key:", apiKey); // Removed for security
      const chatClient = StreamChat.getInstance(apiKey);

      const user = {
        id: userChatId,
        name: session.data?.user?.is_admin ? "Admin" : session.data?.user?.name || "User",
      };

      try {
        await chatClient.connectUser(user, userChatToken);

        setClient(chatClient);
        setIsConnected(true);
        hasInitialized.current = true; // Mark as initialized
        connectionAttempts.current = 0; // Reset on successful connection

        // Reset rate limit backoff on successful connection
        rateLimitBackoffMs.current = 1000;
        lastRateLimitTime.current = 0;
        isConnecting.current = false;

        // Restore channel subscriptions after successful connection
        restoreChannelSubscriptions().catch((error) => {});

        // Restore channels from sessionStorage after a delay to ensure client is ready
        setTimeout(() => {
          // Load channels from database (primary source) - only if not already loaded
          if (!subscribedChannels.size) {
            loadUserChannelsFromDatabase();
          }
          // Fallback to sessionStorage if database fails
          restoreChannelsFromStorage();
        }, 2000);

        chatClient.on((event) => {
          switch (event.type) {
            case "message.new":
            case "notification.message_new":
              // Update last message timestamp in MongoDB
              if (event.message?.cid) {
                updateLastMessageTimestamp(event.message.cid);
              }

              if (event.message?.user?.id !== userChatId) {
                // Get the actual sender's name from the message
                // Try to get a proper name from the message data first
                let senderName =
                  event.message?.user?.name ||
                  event.user?.name ||
                  event.message?.user?.id ||
                  "Someone";

                // If the name is 'Admin' or looks like an ID, we'll let the notification handler fetch the real name
                if (
                  senderName === "Admin" ||
                  senderName === "admin" ||
                  (senderName && senderName.includes("_"))
                ) {
                  // Keep the sender ID for the notification handler to resolve
                  senderName = event.message?.user?.id || event.user?.id || "User";
                }

                // Dispatch custom event for auto-open functionality
                // Try different ways to get the channel ID
                let channelId = event.channel?.id;
                if (!channelId && event.message?.cid) {
                  channelId = event.message.cid;
                }
                if (!channelId && event.channel_id) {
                  channelId = event.channel_id;
                }

                // Create notification for new message
                if (event.message?.user?.id !== userChatId && channelId) {
                  createNotificationForMessage(event.message, senderName, channelId);
                }
                // Try to get from active channels if available
                if (
                  !channelId &&
                  chatClient.activeChannels &&
                  Object.keys(chatClient.activeChannels).length > 0
                ) {
                  const firstActiveChannel = Object.values(chatClient.activeChannels)[0];
                  channelId = firstActiveChannel.id;
                }

                // Ensure user is subscribed to this channel for receiving messages
                if (channelId) {
                  ensureChannelSubscription(channelId).catch((error) => {});
                } else {
                  // If no channel ID found, try to create a channel with the sender
                  const senderId = event.message?.user?.id || event.user?.id;
                  if (senderId && senderId !== userChatId) {
                    // Try to get sender name
                    const senderName = event.message?.user?.name || event.user?.name || senderId;

                    // Create channel directly
                    createChannelDirectly(senderId, senderName)
                      .then((channel) => {
                        if (channel) {
                          // Now dispatch the event with the new channel ID
                          const newChannelId = channel.id;
                          const customEvent = new CustomEvent("stream-chat-message-new", {
                            detail: {
                              channelId: newChannelId,
                              senderName: senderName,
                              message: event.message,
                              senderId: senderId,
                            },
                          });

                          window.dispatchEvent(customEvent);
                        }
                      })
                      .catch((error) => {});
                  }
                }

                // Play notification sound - try immediately, fallback to user interaction
                if (soundEnabled) {
                  // Use the new safe audio playback function
                  playNotificationSoundSafely();
                } else {
                }

                const customEvent = new CustomEvent("stream-chat-message-new", {
                  detail: {
                    channelId: channelId || "unknown",
                    senderName: senderName,
                    message: event.message,
                    senderId: event.message?.user?.id || event.user?.id, // Pass sender ID for name resolution
                  },
                });

                window.dispatchEvent(customEvent);
              }
              break;

            case "message.read":
              // console.log("Message read event:", event); // Removed for security
              // if (event.user?.id === user_chat_id) {
              //     dispatch(SetAccountSliceBits({ bitToSet: "new_message", value: false }));
              //     dispatch(SetAccountSliceBits({
              //         bitToSet: "total_unread_count",
              //         value: event.total_unread_count ?? 0,
              //     }));
              // }
              break;

            case "connection.changed":
              if (!event.online) {
                setIsConnected(false);
              }
              break;

            default:
              break;
          }
        });

        // console.log("%c Stream chat user connected", "color: green;"); // Removed for security
      } catch (error: any) {
        // Log specific error details for debugging
        if (error?.StatusCode === 429 || error?.code === 9) {
          console.log("GetStreamProvider: Rate limit error detected:", {
            code: error.code,
            statusCode: error.StatusCode,
            message: error.message,
            userChatId,
            connectionAttempts: connectionAttempts.current,
          });
        }

        setIsConnected(false);
        isConnecting.current = false;

        // Handle rate limiting specifically
        if (error?.StatusCode === 429 || error?.code === 9) {
          const now = Date.now();
          const timeSinceLastRateLimit = now - lastRateLimitTime.current;

          // If we hit rate limit again within 30 seconds, increase backoff
          if (timeSinceLastRateLimit < 30000) {
            rateLimitBackoffMs.current = Math.min(rateLimitBackoffMs.current * 2, 30000); // Max 30 seconds
          } else {
            // Reset backoff if enough time has passed
            rateLimitBackoffMs.current = 1000;
          }

          lastRateLimitTime.current = now;

          console.log(
            `GetStreamProvider: Rate limit hit (code ${error.code || error.StatusCode}), backing off for ${rateLimitBackoffMs.current}ms`
          );

          // Retry with exponential backoff for rate limits
          if (connectionAttempts.current < maxConnectionAttempts) {
            setTimeout(() => {
              initClient();
            }, rateLimitBackoffMs.current);
          }
        } else {
          // Only show error to admin users for non-rate-limit errors

          // Retry connection after a delay if we haven't exceeded max attempts
          if (connectionAttempts.current < maxConnectionAttempts) {
            setTimeout(() => {
              initClient();
            }, 5000);
          }
        }
      }
    };

    initClient();

    // Clean up on unmount
    return () => {
      if (client) {
        client.disconnectUser();
        setIsConnected(false);
      }
    };
  }, [apiKey, userChatId, userChatToken, session.data?.user]);

  useEffect(() => {
    if (
      session.status === "authenticated" &&
      session.data?.user?.entity_id &&
      (!userChatId || !userChatToken)
    ) {
      dispatch(getUserChatId());
    }
  }, [session.status, session.data?.user?.entity_id]);

  // Sync notifications when user comes online (optimized)
  useEffect(() => {
    if (session.status === "authenticated" && session.data?.user?.entity_id && userChatId) {
      // Optimization: Debounce sync calls to prevent multiple rapid calls
      const syncTimeout = setTimeout(() => {
        syncNotificationsForUserOptimized(session.data.user.entity_id);
      }, 1000); // 1 second debounce

      return () => clearTimeout(syncTimeout);
    }
  }, [session.status, session.data?.user?.entity_id, userChatId]);

  // Optimized function to sync notifications for a user
  const syncNotificationsForUserOptimized = async (userId: number) => {
    try {
      // Optimization: Check if we already synced recently
      const lastSyncKey = `lastSync_${userId}`;
      const lastSync = sessionStorage.getItem(lastSyncKey);
      const now = Date.now();

      if (lastSync && now - parseInt(lastSync) < 5 * 60 * 1000) {
        return;
      }

      const response = await fetch("/next/api/stream-chat/sync", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: userId.toString(),
          lastSyncTime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // Last 24 hours
        }),
      });

      if (response.ok) {
        const result = await response.json();

        // Optimization: Store sync timestamp
        sessionStorage.setItem(lastSyncKey, now.toString());
      } else if (response.status === 429) {
      } else {
      }
    } catch (error) {}
  };

  // Function to clear all channels and start fresh
  const clearAllChannels = async () => {
    if (!client) {
      return;
    }

    try {
      // Clear sessionStorage
      sessionStorage.removeItem("userChannels");

      // Clear subscribed channels state
      setSubscribedChannels(new Set());

      // Try to unwatch all active channels
      if (client.activeChannels) {
        for (const channelId in client.activeChannels) {
          try {
            const channel = client.activeChannels[channelId];
            await channel.stopWatching();
          } catch (error: any) {}
        }
      }
    } catch (error: any) {}
  };

  // Comprehensive channel creation and management system
  const createOrGetChannel = async (recipientId: string, recipientName: string) => {
    return await createCleanChannel(recipientId, recipientName);
  };

  // Debug function to check sessionStorage contents
  const debugSessionStorage = () => {
    const storedChannels = JSON.parse(sessionStorage.getItem("userChannels") || "[]");

    console.log("GetStreamProvider: Current client state:", {
      hasClient: !!client,
      isConnected,
      userChatId,
      activeChannelsCount: client ? Object.keys(client.activeChannels || {}).length : 0,
      activeChannelIds: client ? Object.keys(client.activeChannels || {}) : [],
    });

    return storedChannels;
  };

  // Force restoration function for testing
  const forceRestoreChannels = async () => {
    console.log("GetStreamProvider: Force restoring channels");

    if (!client || !userChatId) {
      console.error("GetStreamProvider: Cannot force restore - no client or userChatId");
      return;
    }

    // First check sessionStorage
    const storedChannels = debugSessionStorage();

    if (storedChannels.length === 0) {
      console.log("GetStreamProvider: No channels in sessionStorage to restore");
      return;
    }

    // Force restore each channel
    for (const channelInfo of storedChannels) {
      const { channelId, recipientId, recipientName } = channelInfo;
      console.log("GetStreamProvider: Force restoring channel:", channelId);

      try {
        const channel = await createOrGetChannel(recipientId, recipientName);
        if (channel) {
          console.log("GetStreamProvider: Successfully force restored channel:", channelId);
        } else {
          console.error("GetStreamProvider: Failed to force restore channel:", channelId);
        }
      } catch (error: any) {
        console.error("GetStreamProvider: Error force restoring channel:", channelId, error);
      }
    }
  };

  // Simple function to force subscribe to a channel immediately
  const forceSubscribeToChannel = async (recipientId: string) => {
    if (!client || !userChatId) {
      return false;
    }

    try {
      // Create channel ID
      const userIds = [userChatId, recipientId].sort();
      const channelId = `messaging_${userIds[0]}_${userIds[1]}`;

      // Get or create channel with proper member setup
      const channel = client.channel("messaging", channelId, {
        members: [userChatId, recipientId],
        created_by_id: userChatId, // Ensure current user is the creator
      });

      // Watch the channel first
      await channel.watch();

      // Ensure current user is added as a member with proper permissions
      try {
        await channel.addMembers([userChatId], {
          user_id: userChatId,
        });
      } catch (memberError: any) {}

      // Add to subscribed channels
      setSubscribedChannels((prev) => new Set(prev).add(channelId));

      // Store in sessionStorage
      const channelInfo = {
        channelId,
        recipientId,
        recipientName: `User ${recipientId}`,
        createdAt: new Date().toISOString(),
        lastAccessed: new Date().toISOString(),
      };

      const existingChannels = JSON.parse(sessionStorage.getItem("userChannels") || "[]");
      const updatedChannels = [
        ...existingChannels.filter((c: any) => c.channelId !== channelId),
        channelInfo,
      ];
      sessionStorage.setItem("userChannels", JSON.stringify(updatedChannels));

      return true;
    } catch (error: any) {
      return false;
    }
  };

  // Function to delete all channels from Stream Chat (nuclear option)
  const deleteAllChannelsFromStream = async () => {
    if (!client || !userChatId) {
      return false;
    }

    try {
      // Get all channels for this user
      const filter = { members: { $in: [userChatId] } };
      const channels = await client.queryChannels(filter, [], { limit: 100 });

      // Delete each channel
      for (const channel of channels) {
        try {
          await channel.delete();
        } catch (error: any) {}
      }

      // Clear local state
      setSubscribedChannels(new Set());
      sessionStorage.removeItem("userChannels");

      return true;
    } catch (error: any) {
      return false;
    }
  };

  // Updated createCleanChannel to use server-side API and ensure single channel per user pair
  const createCleanChannel = async (recipientId: string, recipientName: string) => {
    if (!userChatId) {
      return null;
    }

    try {
      // Check if we already have a channel with this user
      const existingChannels = JSON.parse(sessionStorage.getItem("userChannels") || "[]");
      const existingChannel = existingChannels.find(
        (c: any) => c.recipientId === recipientId || c.channelId.includes(recipientId)
      );

      if (existingChannel) {
        // Ensure we're subscribed to the existing channel
        await subscribeToChannel(existingChannel.channelId, recipientId, recipientName);
        return existingChannel.channelId;
      }

      // Use the server-side API to create/get channel (ensures single channel per user pair)
      const channelId = await createChannelViaAPI(recipientId, recipientName);

      if (channelId) {
        return channelId;
      } else {
        return null;
      }
    } catch (error: any) {
      return null;
    }
  };

  // Test function to verify channel works for both users
  const testChannelForBothUsers = async (recipientId: string) => {
    if (!client || !userChatId) {
      return false;
    }

    try {
      // Create channel
      const channel = await createCleanChannel(recipientId, "Test User");
      if (!channel) {
        return false;
      }

      const channelId = channel.id;

      // Check channel members
      const members = Object.keys(channel.state.members || {});

      // Verify both users are members
      const hasCurrentUser = members.includes(userChatId);
      const hasRecipient = members.includes(recipientId);

      // Test sending message
      try {
        const messageResponse = await channel.sendMessage({
          text: "Test message from both users test",
        });
      } catch (messageError: any) {
        return false;
      }

      // Check current subscription state
      const state = (window as any).getStreamProviderState();

      const success = hasCurrentUser && hasRecipient && state.subscribedChannelsCount > 0;

      return success;
    } catch (error: any) {
      return false;
    }
  };

  // Test function to work with Stream Chat's automatic channel system
  const testStreamChatChannelSystem = async () => {
    if (!client || !userChatId) {
      return false;
    }

    try {
      // Check current state
      const state = (window as any).getStreamProviderState();

      // Look for Stream Chat created channels
      if (client.activeChannels) {
        for (const channelId in client.activeChannels) {
          const channel = client.activeChannels[channelId];

          // If this is a Stream Chat created channel, ensure we're subscribed
          if (channelId.startsWith("!members-") || channelId.includes("messaging_")) {
            await ensureChannelSubscription(channelId);
          }
        }
      }

      // Check final state
      const finalState = (window as any).getStreamProviderState();

      const success = finalState.subscribedChannelsCount > 0;

      return success;
    } catch (error: any) {
      return false;
    }
  };

  // Function to discover and subscribe to all channels the user should be part of
  const discoverAndSubscribeToUserChannels = async () => {
    if (!client || !userChatId) {
      return;
    }

    try {
      // Try multiple approaches to find channels
      let channels: any[] = [];

      // Approach 1: Query channels where user is a member
      try {
        const filter = { members: { $in: [userChatId] } };
        const sort = [{ last_message_at: -1 }];
        const options = { limit: 50 };

        channels = await client.queryChannels(filter, sort, options);
      } catch (error: any) {}

      // Approach 2: If no channels found, try querying all channels
      if (channels.length === 0) {
        try {
          const filter = { type: "messaging" };
          const sort = [{ last_message_at: -1 }];
          const options = { limit: 20 };

          channels = await client.queryChannels(filter, sort, options);

          // Filter channels where current user is actually a member
          channels = channels.filter((channel) => {
            const members = Object.keys(channel.state.members || {});
            return members.includes(userChatId);
          });
        } catch (error: any) {}
      }

      // Approach 3: If still no channels, try to find channels by user ID patterns
      if (channels.length === 0) {
        try {
          // Extract user ID without prefix
          const userIdWithoutPrefix = userChatId.includes("_")
            ? userChatId.split("_")[1]
            : userChatId;

          // Try to find channels that might contain this user ID
          const possibleChannelIds = [
            `messaging_${userChatId}_5731749223_679714`,
            `messaging_5731749223_679714_${userChatId}`,
            `messaging_${userChatId}_3549947`,
            `messaging_3549947_${userChatId}`,
            `messaging_${userChatId}_679714`,
            `messaging_679714_${userChatId}`,
          ];

          for (const channelId of possibleChannelIds) {
            try {
              const channel = client.channel("messaging", channelId);
              await channel.watch();

              channels.push(channel);
            } catch (error: any) {}
          }
        } catch (error: any) {}
      }

      // Process found channels
      for (const channel of channels) {
        const channelId = channel.id;

        // Check if already subscribed
        if (subscribedChannels.has(channelId)) {
          continue;
        }

        try {
          // Ensure the channel is being watched
          if (!channel.state.isUpToDate) {
            await channel.watch();
          }

          // Ensure current user is a member
          const members = Object.keys(channel.state.members || {});
          if (!members.includes(userChatId)) {
            try {
              await channel.addMembers([userChatId], { user_id: userChatId });
            } catch (memberError: any) {
              // User already member or error adding
            }
          }

          // Add to subscribed channels
          setSubscribedChannels((prev) => new Set(prev).add(channelId));

          // Get other members (excluding current user)
          const updatedMembers = Object.keys(channel.state.members || {});
          const otherMembers = updatedMembers.filter((memberId) => memberId !== userChatId);
          const recipientId = otherMembers[0] || "unknown";

          // Store channel info in sessionStorage
          const channelInfo = {
            channelId,
            recipientId,
            recipientName: `User ${recipientId}`,
            createdAt: new Date().toISOString(),
            lastAccessed: new Date().toISOString(),
            isStreamChatChannel: true,
            discovered: true, // Mark as discovered, not manually created
          };

          const existingChannels = JSON.parse(sessionStorage.getItem("userChannels") || "[]");
          const updatedChannels = [
            ...existingChannels.filter((c: any) => c.channelId !== channelId),
            channelInfo,
          ];
          sessionStorage.setItem("userChannels", JSON.stringify(updatedChannels));
        } catch (error: any) {
          console.error(
            "GetStreamProvider: Error subscribing to discovered channel:",
            channelId,
            error
          );
        }
      }
    } catch (error: any) {}
  };

  // Function to force add user to channels they should have access to
  const forceAddUserToChannels = async () => {
    if (!client || !userChatId) {
      return false;
    }

    try {
      // Get the other user IDs we know about
      const otherUserIds = ["5731749223_679714", "5731749223_3549947"];
      const currentUserId = userChatId;

      let successCount = 0;

      for (const otherUserId of otherUserIds) {
        if (otherUserId === currentUserId) continue;

        try {
          // Create channel ID (sorted to ensure consistency)
          const userIds = [currentUserId, otherUserId].sort();
          const channelId = `messaging_${userIds[0]}_${userIds[1]}`;

          // Try to get the channel
          const channel = client.channel("messaging", channelId);

          try {
            // Try to watch the channel
            await channel.watch();

            // Check if current user is a member
            const members = Object.keys(channel.state.members || {});

            if (!members.includes(currentUserId)) {
              try {
                await channel.addMembers([currentUserId], { user_id: currentUserId });

                successCount++;
              } catch (addError: any) {
                console.log(
                  "GetStreamProvider: Error adding user to channel:",
                  channelId,
                  addError.message
                );
              }
            } else {
              successCount++;
            }

            // Add to subscribed channels
            setSubscribedChannels((prev) => new Set(prev).add(channelId));
          } catch (watchError: any) {
            // Try to create the channel if it doesn't exist
            try {
              const newChannel = client.channel("messaging", channelId, {
                members: [currentUserId, otherUserId],
                created_by_id: currentUserId,
              });

              await newChannel.watch();

              // Add to subscribed channels
              setSubscribedChannels((prev) => new Set(prev).add(channelId));
              successCount++;
            } catch (createError: any) {}
          }
        } catch (error: any) {}
      }

      return successCount > 0;
    } catch (error: any) {
      return false;
    }
  };

  // Server-side channel creation and management
  const createChannelViaAPI = async (recipientId: string, recipientName: string) => {
    if (!userChatId) {
      return null;
    }

    try {
      const response = await fetch("/next/api/stream-chat/channel", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user1Id: userChatId,
          user2Id: recipientId,
          user1Name: "Current User", // You can get this from user data
          user2Name: recipientName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        return null;
      }

      const data = await response.json();

      if (data.channelId) {
        // Subscribe to the channel
        await subscribeToChannel(data.channelId, recipientId, recipientName);
        return data.channelId;
      }

      return null;
    } catch (error: any) {
      return null;
    }
  };

  // Subscribe to a channel
  const subscribeToChannel = async (
    channelId: string,
    recipientId: string,
    recipientName: string
  ) => {
    if (!client || !channelId) {
      return false;
    }

    try {
      // Check if already subscribed
      if (subscribedChannels.has(channelId)) {
        return true;
      }

      // Clean the channel ID - remove the channel type prefix if present
      let cleanChannelId = channelId;
      if (channelId.includes(":")) {
        cleanChannelId = channelId.split(":")[1];
      }

      // Get the channel
      const channel = client.channel("messaging", cleanChannelId);

      // Try to watch the channel with multiple attempts
      let watchSuccess = false;
      let lastError: any = null;

      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const watchPromise = channel.watch();
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error("Channel watch timeout")), 15000);
          });

          await Promise.race([watchPromise, timeoutPromise]);

          // Verify the channel is accessible
          if (channel.state.isUpToDate) {
            watchSuccess = true;

            break;
          } else {
            lastError = new Error("Channel not up to date");
          }
        } catch (error: any) {
          lastError = error;

          // Wait before retry
          if (attempt < 3) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
        }
      }

      if (!watchSuccess) {
        return false;
      }

      // Add to subscribed channels
      setSubscribedChannels((prev) => new Set(prev).add(cleanChannelId));

      // Store channel info in sessionStorage
      const channelInfo = {
        channelId: cleanChannelId,
        recipientId,
        recipientName,
        createdAt: new Date().toISOString(),
        lastAccessed: new Date().toISOString(),
        isServerCreated: true,
      };

      const existingChannels = JSON.parse(sessionStorage.getItem("userChannels") || "[]");
      const updatedChannels = [
        ...existingChannels.filter((c: any) => c.channelId !== cleanChannelId),
        channelInfo,
      ];
      sessionStorage.setItem("userChannels", JSON.stringify(updatedChannels));

      return true;
    } catch (error: any) {
      return false;
    }
  };

  // Load user channels from database
  const loadUserChannelsFromDatabase = async () => {
    if (!userChatId) {
      return { success: false, channelsCount: 0 };
    }

    // Prevent multiple simultaneous calls
    if (isConnecting.current) {
      return { success: false, channelsCount: 0, error: "Already loading" };
    }

    try {
      isConnecting.current = true; // Set flag to prevent duplicate calls

      // Use the conversations API which is more reliable
      const response = await fetch(`/next/api/stream-chat/conversations`);

      if (!response.ok) {
        return { success: false, channelsCount: 0, error: `HTTP ${response.status}` };
      }

      const data = await response.json();

      if (!data.conversations || data.conversations.length === 0) {
        return { success: true, channelsCount: 0 };
      }

      let successCount = 0;
      let totalAttempts = 0;

      // Subscribe to all channels from database
      for (const conversation of data.conversations) {
        const { channelId, otherUser } = conversation;
        totalAttempts++;

        if (!channelId) {
          continue;
        }

        try {
          const success = await subscribeToChannel(
            channelId,
            otherUser?.id || "unknown",
            otherUser?.name || "User"
          );
          if (success) {
            successCount++;
          } else {
          }
        } catch (error: any) {
          console.error(
            "GetStreamProvider: Error subscribing to database channel:",
            channelId,
            error
          );
        }
      }

      return {
        success: true,
        channelsCount: data.conversations.length,
        subscribedCount: successCount,
      };
    } catch (error: any) {
      return { success: false, channelsCount: 0, error: error.message };
    } finally {
      isConnecting.current = false; // Reset flag when done
    }
  };

  // Update last message timestamp in MongoDB
  const updateLastMessageTimestamp = async (channelId: string) => {
    try {
      const response = await fetch("/next/api/stream-chat/channel", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ channelId }),
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
    } catch (error: any) {}
  };

  return (
    <>
      {client ? (
        <Chat client={client}>{children}</Chat>
      ) : (
        <React.Fragment>{children}</React.Fragment>
      )}
    </>
  );
};
